import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  BookOpen, Zap, RefreshCw, Camera, Brain, Sparkles, Filter, LineChart,
  Plus, Loader2, Trash2, TrendingUp, TrendingDown, Trophy, DollarSign, Target, Clock3, Smartphone,
} from "lucide-react";

const EMOTIONS = ["Calm", "Confident", "Fearful", "Greedy", "FOMO", "Revenge", "Disciplined", "Anxious", "Neutral"];
const SESSIONS = ["Asian", "London", "New York"];
const RESULTS = ["win", "loss", "breakeven"];

const FEATURES = [
  { Icon: Zap, t: "Quick Trade Entry", d: "Log trades in seconds with an intuitive form. Pre-filled templates make it even faster." },
  { Icon: RefreshCw, t: "Automated Journaling", d: "Import trades from connected accounts — your journal updates in real time.", soon: true },
  { Icon: Camera, t: "Screenshot & Charts", d: "Attach screenshots to every trade. Visual context helps you learn from each decision." },
  { Icon: Brain, t: "Emotion Tracking", d: "Record your state before, during and after each trade. See how psychology drives results." },
  { Icon: Sparkles, t: "AI-Generated Insights", d: "Instant coaching on every trade. AI surfaces patterns, mistakes and opportunities." },
  { Icon: Filter, t: "Advanced Filtering", d: "Filter by pair, session, strategy and result. Find exactly what you need instantly." },
  { Icon: LineChart, t: "Performance Patterns", d: "Discover which strategies, sessions and pairs work best for you. Data-driven improvement." },
];

const STEPS = [
  { n: "1", t: "Log Your Trade", d: "Enter details, attach a screenshot and record your emotional state. Under 30 seconds." },
  { n: "2", t: "Get AI Insights", d: "Our AI analyses execution and psychology and gives instant, actionable feedback." },
  { n: "3", t: "Track Patterns", d: "Review performance over time and see which setups, sessions and pairs win most." },
];

const WHY = [
  { t: "Identify Your Edge", d: "Track what works and what doesn't. Build on strengths, cut weaknesses." },
  { t: "Improve Psychology", d: "See how emotions affect trading and build mental discipline over time." },
  { t: "Learn From Mistakes", d: "Review losing trades with AI insight — turn losses into lessons." },
  { t: "Build Consistency", d: "Track progress in real numbers and patterns, not vibes." },
];

const emptyForm = {
  pair: "", direction: "long", strategy: "", session: "London",
  entry_price: "", exit_price: "", lots: "", result: "win", pnl: "", rr: "",
  emotion_before: "Disciplined", emotion_during: "Calm", emotion_after: "Confident",
  notes: "", screenshot_base64: null, trade_date: "",
};

const EmotionSelect = ({ label, value, onChange, testId }) => (
  <div className="space-y-1.5">
    <Label className="font-data text-[10px] uppercase tracking-widest text-slate-500">{label}</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testId} className="bg-[#0C1017] border-white/10 h-10 text-white"><SelectValue /></SelectTrigger>
      <SelectContent className="bg-[#0C1017] border-white/10 text-white">
        {EMOTIONS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

const StatCard = ({ icon: Icon, label, value, sub, accent, testId }) => (
  <div data-testid={testId} className="card-surface rounded-xl p-4">
    <div className="flex items-center justify-between">
      <span className="font-data text-[10px] uppercase tracking-widest text-slate-500">{label}</span>
      <Icon className={`h-4 w-4 ${accent || "text-brand"}`} />
    </div>
    <div className={`font-data text-2xl font-bold mt-2 ${accent || "text-white"}`}>{value}</div>
    {sub && <div className="font-data text-[10px] text-slate-500 mt-0.5">{sub}</div>}
  </div>
);

export const TradeJournal = () => {
  const [stats, setStats] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [insightBusy, setInsightBusy] = useState(null);
  const [filters, setFilters] = useState({ pair: "All", session: "All", result: "All" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => v !== "All" && params.append(k, v));
      const [s, e] = await Promise.all([
        api.get("/journal/stats"),
        api.get(`/journal?${params.toString()}`),
      ]);
      setStats(s.data);
      setEntries(e.data);
    } catch (err) { console.error("Failed to load journal:", err); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onScreenshot = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const max = 1200;
        let { width, height } = img;
        if (width > max || height > max) {
          const s = max / Math.max(width, height);
          width = Math.round(width * s);
          height = Math.round(height * s);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        set("screenshot_base64", canvas.toDataURL("image/jpeg", 0.8));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!form.pair.trim()) { toast.error("Enter a pair"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        entry_price: form.entry_price ? parseFloat(form.entry_price) : null,
        exit_price: form.exit_price ? parseFloat(form.exit_price) : null,
        lots: form.lots ? parseFloat(form.lots) : null,
        pnl: form.pnl ? parseFloat(form.pnl) : 0,
      };
      await api.post("/journal", payload);
      toast.success("Trade logged");
      setOpen(false);
      setForm(emptyForm);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const genInsight = async (id) => {
    setInsightBusy(id);
    try {
      const { data } = await api.post(`/journal/${id}/insight`);
      setEntries((prev) => prev.map((en) => (en.id === id ? { ...en, ai_insight: data.ai_insight } : en)));
      toast.success("AI insight ready");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Insight failed");
    } finally { setInsightBusy(null); }
  };

  const remove = async (id) => {
    setEntries((prev) => prev.filter((e) => e.id !== id));
    try { await api.delete(`/journal/${id}`); load(); } catch (e) { load(); }
  };

  const pairsInUse = ["All", ...Array.from(new Set(entries.map((e) => e.pair)))];

  return (
    <div className="space-y-10">
      {/* Marketing hero */}
      <div className="relative overflow-hidden rounded-2xl card-surface grain-overlay">
        <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-brand/10 blur-3xl" />
        <div className="relative p-8 lg:p-10">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-brand/30 bg-brand/10 font-data text-[10px] uppercase tracking-widest text-brand">
            <BookOpen className="h-3.5 w-3.5" /> Trade Journal
          </span>
          <h2 className="mt-4 font-heading text-3xl lg:text-4xl font-black tracking-tight max-w-2xl leading-[1.05]">
            Track Every Trade. <span className="text-brand">Improve Every Day.</span>
          </h2>
          <p className="mt-3 text-slate-400 max-w-2xl leading-relaxed">
            The most powerful trade journal for serious traders. Log trades, track emotions, and get AI-powered
            insights that help you become consistently profitable.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button data-testid="journal-start-cta" onClick={() => setOpen(true)} className="bg-brand hover:bg-brand-hover text-white font-semibold h-11 px-5 gap-2">
              <Plus className="h-4 w-4" /> Start Journaling Free
            </Button>
            <Button variant="outline" className="bg-transparent border-white/15 text-white hover:bg-white/5 h-11 px-5 gap-2">
              <Smartphone className="h-4 w-4" /> Download App
            </Button>
          </div>
        </div>
      </div>

      {/* Feature grid */}
      <div>
        <h3 className="font-heading text-xl font-bold">Everything You Need to Journal Like a Pro</h3>
        <p className="text-slate-400 text-sm mt-1">Built specifically for traders who are serious about improvement.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
          {FEATURES.map((f, i) => (
            <motion.div key={f.t} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.4, delay: (i % 3) * 0.05 }} className="card-surface rounded-xl p-5">
              <div className="flex items-center gap-2.5">
                <div className="h-9 w-9 rounded-lg border border-white/10 bg-white/5 grid place-items-center"><f.Icon className="h-5 w-5 text-brand" /></div>
                <h4 className="font-semibold text-sm text-white flex items-center gap-2">{f.t}
                  {f.soon && <span className="font-data text-[8px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-amber/15 text-amber border border-amber/30">Soon</span>}
                </h4>
              </div>
              <p className="text-sm text-slate-400 mt-2 leading-relaxed">{f.d}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* How it works */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {STEPS.map((s) => (
          <div key={s.n} className="card-surface rounded-xl p-5 flex gap-4">
            <span className="h-9 w-9 shrink-0 rounded-full bg-brand text-white font-data font-bold grid place-items-center">{s.n}</span>
            <div><h4 className="font-semibold text-white text-sm">{s.t}</h4><p className="text-sm text-slate-400 mt-1">{s.d}</p></div>
          </div>
        ))}
      </div>

      {/* Performance patterns */}
      <div>
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <LineChart className="h-5 w-5 text-brand" />
            <h3 className="font-heading text-xl font-bold">Your Performance Patterns</h3>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="journal-add-trade-btn" className="bg-brand hover:bg-brand-hover text-white font-semibold gap-2 h-10"><Plus className="h-4 w-4" /> Log Trade</Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0C1017] border-white/10 text-white max-w-lg max-h-[88vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading text-xl">Log a Trade</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-1">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label className="font-data text-[10px] uppercase tracking-widest text-slate-500">Pair</Label>
                    <Input data-testid="journal-pair-input" value={form.pair} onChange={(e) => set("pair", e.target.value)} placeholder="EUR/USD" className="bg-[#070A0E] border-white/10 h-10 text-white font-data" /></div>
                  <div className="space-y-1.5"><Label className="font-data text-[10px] uppercase tracking-widest text-slate-500">Direction</Label>
                    <Select value={form.direction} onValueChange={(v) => set("direction", v)}>
                      <SelectTrigger data-testid="journal-direction-select" className="bg-[#070A0E] border-white/10 h-10 text-white"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#0C1017] border-white/10 text-white"><SelectItem value="long">Long</SelectItem><SelectItem value="short">Short</SelectItem></SelectContent>
                    </Select></div>
                  <div className="space-y-1.5"><Label className="font-data text-[10px] uppercase tracking-widest text-slate-500">Session</Label>
                    <Select value={form.session} onValueChange={(v) => set("session", v)}>
                      <SelectTrigger className="bg-[#070A0E] border-white/10 h-10 text-white"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#0C1017] border-white/10 text-white">{SESSIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div className="space-y-1.5"><Label className="font-data text-[10px] uppercase tracking-widest text-slate-500">Strategy</Label>
                    <Input value={form.strategy} onChange={(e) => set("strategy", e.target.value)} placeholder="SMC, Breakout…" className="bg-[#070A0E] border-white/10 h-10 text-white" /></div>
                  <div className="space-y-1.5"><Label className="font-data text-[10px] uppercase tracking-widest text-slate-500">Entry</Label>
                    <Input type="number" value={form.entry_price} onChange={(e) => set("entry_price", e.target.value)} className="bg-[#070A0E] border-white/10 h-10 text-white font-data" /></div>
                  <div className="space-y-1.5"><Label className="font-data text-[10px] uppercase tracking-widest text-slate-500">Exit</Label>
                    <Input type="number" value={form.exit_price} onChange={(e) => set("exit_price", e.target.value)} className="bg-[#070A0E] border-white/10 h-10 text-white font-data" /></div>
                  <div className="space-y-1.5"><Label className="font-data text-[10px] uppercase tracking-widest text-slate-500">Lots</Label>
                    <Input type="number" value={form.lots} onChange={(e) => set("lots", e.target.value)} className="bg-[#070A0E] border-white/10 h-10 text-white font-data" /></div>
                  <div className="space-y-1.5"><Label className="font-data text-[10px] uppercase tracking-widest text-slate-500">R:R</Label>
                    <Input value={form.rr} onChange={(e) => set("rr", e.target.value)} placeholder="1:3" className="bg-[#070A0E] border-white/10 h-10 text-white font-data" /></div>
                  <div className="space-y-1.5"><Label className="font-data text-[10px] uppercase tracking-widest text-slate-500">Result</Label>
                    <Select value={form.result} onValueChange={(v) => set("result", v)}>
                      <SelectTrigger data-testid="journal-result-select" className="bg-[#070A0E] border-white/10 h-10 text-white"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#0C1017] border-white/10 text-white">{RESULTS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div className="space-y-1.5"><Label className="font-data text-[10px] uppercase tracking-widest text-slate-500">P&L ($)</Label>
                    <Input data-testid="journal-pnl-input" type="number" value={form.pnl} onChange={(e) => set("pnl", e.target.value)} className="bg-[#070A0E] border-white/10 h-10 text-white font-data" /></div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <EmotionSelect label="Before" value={form.emotion_before} onChange={(v) => set("emotion_before", v)} testId="journal-emotion-before" />
                  <EmotionSelect label="During" value={form.emotion_during} onChange={(v) => set("emotion_during", v)} testId="journal-emotion-during" />
                  <EmotionSelect label="After" value={form.emotion_after} onChange={(v) => set("emotion_after", v)} testId="journal-emotion-after" />
                </div>

                <div className="space-y-1.5"><Label className="font-data text-[10px] uppercase tracking-widest text-slate-500">Notes</Label>
                  <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="What was your thesis? What did you see?" className="bg-[#070A0E] border-white/10 text-white min-h-[70px] resize-none" /></div>

                <div className="space-y-1.5"><Label className="font-data text-[10px] uppercase tracking-widest text-slate-500">Screenshot</Label>
                  <input data-testid="journal-screenshot-input" type="file" accept="image/*" onChange={(e) => onScreenshot(e.target.files?.[0])} className="block w-full text-sm text-slate-400 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-brand/15 file:text-brand file:font-data file:text-xs" />
                  {form.screenshot_base64 && <img src={form.screenshot_base64} alt="" className="mt-2 rounded-lg max-h-32 object-cover" />}
                </div>

                <Button data-testid="journal-save-trade-btn" onClick={submit} disabled={saving} className="w-full h-11 bg-brand hover:bg-brand-hover text-white font-semibold gap-2">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Save Trade
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard testId="journal-winrate-card" icon={Trophy} label="Win Rate" value={`${stats?.win_rate ?? 0}%`} sub={`${stats?.wins || 0}W · ${stats?.losses || 0}L`} accent="text-amber" />
          <StatCard testId="journal-pnl-card" icon={DollarSign} label="Net P&L" value={`$${(stats?.total_pnl ?? 0).toLocaleString()}`} sub={`${stats?.total || 0} trades`} accent={(stats?.total_pnl ?? 0) >= 0 ? "text-gain" : "text-loss"} />
          <StatCard testId="journal-bestpair-card" icon={Target} label="Best Pair" value={stats?.best_pair?.key || "—"} sub={stats?.best_pair ? `$${stats.best_pair.pnl} · ${stats.best_pair.count} trades` : "log trades"} />
          <StatCard testId="journal-bestsession-card" icon={Clock3} label="Best Session" value={stats?.best_session?.key || "—"} sub={stats?.best_session ? `${stats.best_session.win_rate}% win` : "log trades"} />
        </div>

        {stats?.emotion_performance?.length > 0 && (
          <div className="card-surface rounded-xl p-5 mt-4">
            <h4 className="font-heading text-sm font-bold mb-3 flex items-center gap-2"><Brain className="h-4 w-4 text-brand" /> Win rate by pre-trade emotion</h4>
            <div className="space-y-2.5">
              {stats.emotion_performance.map((e) => (
                <div key={e.emotion}>
                  <div className="flex justify-between text-xs mb-1"><span className="text-slate-300">{e.emotion}</span><span className="font-data text-slate-400">{e.win_rate}% · {e.count}</span></div>
                  <div className="h-1.5 rounded-full bg-white/5 overflow-hidden"><div className="h-full rounded-full bg-brand transition-all duration-500" style={{ width: `${e.win_rate}%` }} /></div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Filters + entries */}
      <div>
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <div className="flex items-center gap-2"><Filter className="h-4 w-4 text-slate-500" /><span className="font-data text-[11px] uppercase tracking-widest text-slate-500">Filter</span></div>
          <Select value={filters.pair} onValueChange={(v) => setFilters((f) => ({ ...f, pair: v }))}>
            <SelectTrigger data-testid="journal-filter-pair" className="bg-[#0C1017] border-white/10 h-9 w-36 text-white text-sm"><SelectValue placeholder="Pair" /></SelectTrigger>
            <SelectContent className="bg-[#0C1017] border-white/10 text-white">{pairsInUse.map((p) => <SelectItem key={p} value={p}>{p === "All" ? "All Pairs" : p}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.session} onValueChange={(v) => setFilters((f) => ({ ...f, session: v }))}>
            <SelectTrigger data-testid="journal-filter-session" className="bg-[#0C1017] border-white/10 h-9 w-36 text-white text-sm"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0C1017] border-white/10 text-white"><SelectItem value="All">All Sessions</SelectItem>{SESSIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.result} onValueChange={(v) => setFilters((f) => ({ ...f, result: v }))}>
            <SelectTrigger data-testid="journal-filter-result" className="bg-[#0C1017] border-white/10 h-9 w-36 text-white text-sm"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0C1017] border-white/10 text-white"><SelectItem value="All">All Results</SelectItem>{RESULTS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="py-16 grid place-items-center"><Loader2 className="h-7 w-7 animate-spin text-brand" /></div>
        ) : entries.length === 0 ? (
          <div className="card-surface rounded-xl py-14 text-center">
            <BookOpen className="h-8 w-8 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400 text-sm">No trades logged yet. Click "Log Trade" to start your journal.</p>
          </div>
        ) : (
          <div data-testid="journal-entries-list" className="space-y-3">
            {entries.map((e) => {
              const win = e.result === "win"; const loss = e.result === "loss";
              return (
                <motion.div key={e.id} data-testid="journal-entry-card" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="card-surface rounded-xl p-5">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border font-data text-[11px] font-semibold ${e.direction === "long" ? "text-gain border-gain/30 bg-gain/10" : "text-loss border-loss/30 bg-loss/10"}`}>
                        {e.direction === "long" ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}{e.pair}
                      </span>
                      <span className="font-data text-[11px] text-slate-500">{e.session}{e.strategy ? ` · ${e.strategy}` : ""}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`font-data text-sm font-bold ${win ? "text-gain" : loss ? "text-loss" : "text-slate-300"}`}>{(e.pnl ?? 0) >= 0 ? "+" : ""}${e.pnl}</span>
                      <span className={`font-data text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${win ? "text-gain border-gain/30" : loss ? "text-loss border-loss/30" : "text-slate-400 border-white/10"}`}>{e.result}</span>
                      <button data-testid="journal-delete-btn" onClick={() => remove(e.id)} className="text-slate-600 hover:text-loss transition-colors"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 font-data text-[11px] text-slate-500">
                    {e.entry_price != null && <span>Entry {e.entry_price}</span>}
                    {e.exit_price != null && <span>Exit {e.exit_price}</span>}
                    {e.rr && <span>R:R {e.rr}</span>}
                    <span className="text-brand">😌 {e.emotion_before} → {e.emotion_during} → {e.emotion_after}</span>
                  </div>
                  {e.notes && <p className="text-sm text-slate-300 mt-2">{e.notes}</p>}

                  {e.ai_insight ? (
                    <div className="mt-3 rounded-lg bg-brand/5 border border-brand/20 p-3">
                      <div className="flex items-center gap-1.5 mb-1"><Sparkles className="h-3.5 w-3.5 text-brand" /><span className="font-data text-[10px] uppercase tracking-widest text-brand">AI Coach</span></div>
                      <p className="text-sm text-slate-300 whitespace-pre-line leading-relaxed">{e.ai_insight}</p>
                    </div>
                  ) : (
                    <Button data-testid="journal-ai-insight-btn" onClick={() => genInsight(e.id)} disabled={insightBusy === e.id} variant="outline" size="sm"
                      className="mt-3 bg-transparent border-brand/30 text-brand hover:bg-brand/10 gap-2 h-8">
                      {insightBusy === e.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Get AI Insight
                    </Button>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Why journal */}
      <div className="card-surface rounded-2xl p-8">
        <h3 className="font-heading text-xl font-bold text-center">Why Professional Traders Journal Every Trade</h3>
        <p className="text-slate-400 text-sm text-center mt-1">The difference between amateurs and professionals? Consistent journaling.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          {WHY.map((w) => (
            <div key={w.t} className="text-center">
              <div className="h-10 w-10 rounded-lg bg-brand/15 border border-brand/30 grid place-items-center mx-auto mb-3"><Sparkles className="h-5 w-5 text-brand" /></div>
              <h4 className="font-semibold text-white text-sm">{w.t}</h4>
              <p className="text-sm text-slate-400 mt-1">{w.d}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
